import React from 'react';
import { any, arrayOf, func, string } from 'prop-types';
import { Column, Row } from 'simple-flexbox';
import { createUseStyles, useTheme } from 'react-jss';
import CollapsibleContent from 'components/collapsible/CollapsibleContent';
import { useSidebar } from 'hooks/useSidebar';

const useStyles = createUseStyles({
    activeContainer: {
        backgroundColor: ({ theme }) => `${theme.color.salem700}40`,
        borderRadius: '6px',
        boxShadow:
            '0 4px 12px rgba(0, 0, 0, 0.25), 0 2px 6px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
        border: '1px solid rgba(255, 255, 255, 0.15)',
        paddingLeft: 8,
        transform: 'translateY(-1px)',
        transition: 'all 0.2s ease-out'
    },
    container: ({ collapsed, level }) => ({
        display: 'flex',
        height: 36,
        cursor: 'pointer',
        justifyContent: collapsed ? 'center' : 'flex-start',
        alignItems: 'center',
        '&:hover .menu-title': {
            color: '#ffffff',
            fontWeight: '600'
        },
        '&:hover .menu-icon': {
            color: '#ffffff'
        },
        paddingLeft: ({ level, collapsed }) =>
            collapsed ? 0 : level > 1 ? 48 + 16 * (level - 1) : 32,
        paddingRight: ({ collapsed }) => (collapsed ? 0 : 16),
        transition: 'all 0.15s ease-out',
        borderRadius: '6px',
        margin: '2px 8px',
        backgroundColor: level > 1 ? 'rgba(255, 255, 255, 0.03)' : 'transparent',
        borderLeft: level > 1 ? '2px solid rgba(255, 255, 255, 0.1)' : 'none',
        marginLeft: level > 1 ? '16px' : '8px'
    }),
    leftBar: {
        borderLeft: ({ theme, level }) => (level > 1 ? 'none' : `3px solid ${theme.color.white01}`)
    },
    title: ({ collapsed, isActive, level }) => ({
        fontSize: level > 1 ? '12px' : '13px',
        lineHeight: '14px',
        letterSpacing: '0.3px',
        color: isActive
            ? '#ffffff'
            : level > 1
            ? 'rgba(255, 255, 255, 0.75)'
            : 'rgba(255, 255, 255, 0.85)',
        fontWeight: isActive ? '600' : level > 1 ? '400' : '500',
        marginLeft: collapsed ? 0 : 24,
        opacity: collapsed ? 0 : 1,
        transition: 'all 0.15s ease-out',
        whiteSpace: 'nowrap',
        overflow: 'hidden'
    }),
    iconContainer: ({ collapsed }) => ({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 24,
        height: 24,
        minWidth: 24,
        transition: 'all 0.15s ease-out'
    })
});

function MenuItemComponent({
    children,
    icon: Icon,
    id,
    items = [],
    level = 1,
    onClick,
    title,
    collapsed = false
}) {
    const theme = useTheme();
    const isCollapsible = children && children.length > 0 && !collapsed;
    const { isExpanded, isActive, onItemClick } = useSidebar({
        isCollapsible,
        item: id,
        items
    });
    const classes = useStyles({ theme, level, isActive, collapsed });
    const classNameColumn = isActive && !collapsed ? classes.leftBar : '';
    const classNameContainer = [classes.container, isActive && classes.activeContainer].join(' ');
    const iconColor = isActive ? theme.color.white01 : theme.color.white01;

    function onItemClicked(e) {
        if (onClick) {
            onClick(e);
        }
        onItemClick();
    }

    return (
        <Column key={id} className={classNameColumn}>
            <Row
                vertical='center'
                onClick={onItemClicked}
                className={classNameContainer}
                title={collapsed ? title : undefined}
            >
                <div className={classes.iconContainer}>
                    <Icon
                        className='menu-icon'
                        style={{ color: iconColor, opacity: isActive ? 1 : 0.8, fontSize: '18px' }}
                    />
                </div>
                <span className={`${classes.title} menu-title`}>{title}</span>
            </Row>
            {isCollapsible && (
                <CollapsibleContent expanded={isExpanded}>
                    {children.map((child) => child.type({ ...child.props }))}
                </CollapsibleContent>
            )}
        </Column>
    );
}

MenuItemComponent.defaultProps = {};

MenuItemComponent.propTypes = {
    children: any,
    icon: any,
    id: string,
    onClick: func,
    items: arrayOf(string),
    title: string
};

export default MenuItemComponent;
